import { useEffect, useState } from 'react';

// Male / female / male / female alternation. Add new banners here and they join the rotation.
// The art is photo-only: the words are drawn live below, so the second line
// follows the active accent theme instead of being baked into the pixels.
const SLIDES = [
  { base: '/home-hero-n1', headline: ['Stronger habits.', 'A healthier you.'] },
  { base: '/home-hero-n2', headline: ['Train smarter.', 'See real results.'] },
  { base: '/home-hero-n3', headline: ['Cardio that knows', 'your recovery.'] },
  { base: '/home-hero-n11', headline: ['Anywhere you are.', 'Any hour you like.'] },
  { base: '/home-hero-n4', headline: ['Train anywhere.', 'Feel stronger.'] },
  { base: '/home-hero-n5', headline: ['Your plan adapts', 'every week.'] },
  { base: '/home-hero-n6', headline: ['Lift. Log.', 'Watch it climb.'] },
  { base: '/home-hero-n7', headline: ['Sore knee?', 'Coach adapts.'] },
  { base: '/home-hero-n12', headline: ['Snap your plate.', 'Macros counted.'] },
  { base: '/home-hero-n8', headline: ['Fitness that fits', 'your real life.'] },
  { base: '/home-hero-n9', headline: ['Show up today.', 'Keep the streak.'] },
  { base: '/home-hero-n10', headline: ['Every set counted.', 'Every win earned.'] },
];

const HOLD_MS = 3000;
const FADE_MS = 700;

/** Home hero: auto-advancing banner. `brandBlue` locks the headline to blue for landing pages.
  *   First frame loads eagerly, the rest preload after mount. */
export const HomeHeroCarousel = ({ brandBlue = false }) => {
  const [index, setIndex] = useState(0);
  const [ready, setReady] = useState(false);
  const [nudge, setNudge] = useState(0);

  // Tap anywhere on the banner to skip ahead, and restart the hold so the next
  // swap isn't instant. Deliberately undiscoverable: no arrows, dots or cursor change.
  const advance = () => {
    setIndex(i => (i + 1) % SLIDES.length);
    setNudge(n => n + 1);
  };

  useEffect(() => {
    // Warm the remaining frames so each swap is instant, without delaying first paint.
    const id = window.requestIdleCallback
      ? window.requestIdleCallback(() => setReady(true))
      : setTimeout(() => setReady(true), 400);
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(id) : clearTimeout(id));
  }, []);

  useEffect(() => {
    if (SLIDES.length < 2) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const timer = setInterval(() => {
      if (document.hidden) return;
      setIndex(i => (i + 1) % SLIDES.length);
    }, HOLD_MS);
    return () => clearInterval(timer);
  }, [nudge]);

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ aspectRatio: '1905 / 825', containerType: 'inline-size' }}
      onClick={advance}
      data-testid="dashboard-hero-carousel"
      data-slide={index}
    >
      {SLIDES.map((slide, i) => (
        <img
          key={slide.base}
          src={`${slide.base}-800.webp`}
          srcSet={`${slide.base}-800.webp 800w, ${slide.base}-1200.webp 1200w`}
          sizes="(max-width: 768px) 100vw, 768px"
          width={1905}
          height={825}
          alt={i === index ? slide.headline.join(' ') : ''}
          aria-hidden={i === index ? undefined : 'true'}
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ opacity: i === index ? 1 : 0, transition: `opacity ${FADE_MS}ms ease-in-out` }}
          loading={i === 0 ? 'eager' : 'lazy'}
          decoding="async"
          fetchpriority={i === 0 ? 'high' : 'low'}
          data-testid={`dashboard-hero-slide-${i + 1}`}
          data-active={i === index}
          {...(i > 0 && !ready ? { 'data-pending': true } : {})}
        />
      ))}

      {/* Live headline — photo-only art, so the accent line follows the theme. */}
      {SLIDES.map((slide, i) => (
        <div
          key={`h-${slide.base}`}
          className="absolute inset-0 flex items-center pointer-events-none"
          style={{ opacity: i === index ? 1 : 0, transition: `opacity ${FADE_MS}ms ease-in-out` }}
          aria-hidden="true"
          data-testid={`hero-slide-headline-${i + 1}`}
        >
          <span
            className="absolute inset-y-0 left-0 w-[72%]"
            style={{ background: 'linear-gradient(90deg, rgba(6,6,6,0.82) 0%, rgba(6,6,6,0.55) 45%, rgba(6,6,6,0) 100%)' }}
          />
          <h2 className="relative font-heading font-black uppercase leading-[0.92] tracking-[-0.015em] pl-[5.5%] pr-[32%]"
              style={{ fontSize: 'clamp(1rem, 5.9cqw, 2.2rem)' }}>
            <span className="block text-white" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.7)' }}>{slide.headline[0]}</span>
            <span
              className={`block ${brandBlue ? '' : 'text-accent hero-accent-line'}`}
              style={{
                fontSize: '1.26em',
                lineHeight: 0.9,
                textShadow: '0 2px 12px rgba(0,0,0,0.75)',
                ...(brandBlue ? { color: '#0165FE' } : {}),
              }}
            >
              {slide.headline[1]}
            </span>
          </h2>
        </div>
      ))}
    </div>
  );
};

export default HomeHeroCarousel;
